import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box, Tabs, Tab, Card, CardContent, Typography, TextField, Button, Grid,
  MenuItem, Table, TableHead, TableRow, TableCell, TableBody, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert, Chip, Divider,
  Stack, CircularProgress, Tooltip, Paper, TableContainer, useMediaQuery,
} from "@mui/material";
import {
  Delete as DeleteIcon, Download as DownloadIcon, Visibility as ViewIcon,
  UploadFile as UploadIcon, Add as AddIcon, Draw as DrawIcon,
} from "@mui/icons-material";
import api from "../services/api";
import { getToken } from "../services/auth";
import { todayISO } from "../utils/date";

// Build an authenticated GET url (for <img> / window.open, which don't carry
// the axios Authorization header).
function authUrl(path) {
  const base = api.defaults.baseURL || "";
  const url = `${base}${path}`;
  const t = getToken();
  return t ? `${url}${url.includes("?") ? "&" : "?"}token=${encodeURIComponent(t)}` : url;
}

// POST/GET a PDF-producing endpoint and open the result in a new tab.
async function openPdf(method, path, body) {
  const resp = await api.request({ url: path, method, data: body, responseType: "blob" });
  const blob = new Blob([resp.data], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, "_blank");
  if (!w) alert("Popup blocked. Allow popups for this site.");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

/* ------------------------------------------------------------------ */
export default function CompanyDocuments() {
  const [tab, setTab] = useState(0);
  const [profile, setProfile] = useState(null);
  const [signatures, setSignatures] = useState([]);
  const [msg, setMsg] = useState(null);

  const flash = (severity, text) => { setMsg({ severity, text }); setTimeout(() => setMsg(null), 4000); };

  const loadProfile = useCallback(() => {
    api.get("/companyDocuments/profile").then((r) => setProfile(r.data || {})).catch(() => {});
  }, []);
  const loadSignatures = useCallback(() => {
    api.get("/companyDocuments/signatures").then((r) => setSignatures(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => { loadProfile(); loadSignatures(); }, [loadProfile, loadSignatures]);

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Company Documents</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Store the paperwork buyers ask for and generate a filled, signed copy on demand.
      </Typography>
      {msg && <Alert severity={msg.severity} sx={{ mb: 2 }}>{msg.text}</Alert>}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable" scrollButtons="auto">
        <Tab label="Generate" />
        <Tab label="Library" />
        <Tab label="Signatures" />
        <Tab label="Company Info" />
      </Tabs>

      {tab === 0 && <GenerateTab profile={profile} signatures={signatures} flash={flash} />}
      {tab === 1 && <LibraryTab flash={flash} />}
      {tab === 2 && <SignaturesTab signatures={signatures} reload={loadSignatures} flash={flash} />}
      {tab === 3 && <ProfileTab profile={profile} setProfile={setProfile} reload={loadProfile} flash={flash} />}
    </Box>
  );
}

/* ============================ Generate ============================ */
function GenerateTab({ profile, signatures, flash }) {
  const today = todayISO();
  const [rc, setRc] = useState({
    sellerName: "", sellerStreet: "", sellerCityStateZip: "",
    itemsDescription: "", businessActivity: "", signatureId: "", date: today,
  });
  const [busy, setBusy] = useState(false);
  const [w9, setW9] = useState({ status: null, signatureId: "", date: today });
  // Who signs is the same person letter after letter, so it is remembered
  // in this browser rather than retyped each time.
  const rememberedSigner = (() => {
    try { return JSON.parse(localStorage.getItem("letterSigner") || "{}"); }
    catch { return {}; }
  })();
  const [emp, setEmp] = useState({
    employeeName: "", position: "", employmentType: "Full-time",
    startDate: "", endDate: "", payRate: "", payPeriod: "per week",
    addressedTo: "", notes: "", signatureId: "", date: today,
    signerName: rememberedSigner.signerName || "",
    signerTitle: rememberedSigner.signerTitle || "",
  });
  const [lh, setLh] = useState({ body: "", showDate: true, date: today });
  const w9FileRef = useRef();

  useEffect(() => {
    setRc((p) => ({ ...p, businessActivity: p.businessActivity || profile?.DefaultBusinessActivity || "",
      itemsDescription: p.itemsDescription || profile?.DefaultItemsDescription || "" }));
  }, [profile]);

  const loadW9Status = useCallback(() => {
    api.get("/companyDocuments/w9/template").then((r) => setW9((p) => ({ ...p, status: r.data }))).catch(() => {});
  }, []);
  useEffect(() => { loadW9Status(); }, [loadW9Status]);

  const genResale = async (save) => {
    if (!rc.sellerName) { flash("warning", "Enter the seller name."); return; }
    setBusy(true);
    try {
      await openPdf("post", "/companyDocuments/resale-cert/generate", { ...rc, save });
      if (save) flash("success", "Certificate generated and saved to the Library.");
    } catch (e) { flash("error", e?.response?.data?.error || "Failed to generate."); }
    finally { setBusy(false); }
  };

  const genW9 = async (save) => {
    setBusy(true);
    try {
      const qs = new URLSearchParams();
      if (w9.signatureId) qs.set("signatureId", w9.signatureId);
      if (w9.date) qs.set("date", w9.date);
      if (save) qs.set("save", "1");
      await openPdf("get", `/companyDocuments/w9/generate?${qs.toString()}`);
      if (save) flash("success", "W-9 generated and saved to the Library.");
    } catch (e) { flash("error", e?.response?.data?.error || "Failed to generate W-9."); }
    finally { setBusy(false); }
  };

  const uploadW9 = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("DocType", "W9_TEMPLATE");
    fd.append("Title", "W-9 template");
    try {
      await api.post("/companyDocuments/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      flash("success", "W-9 template saved.");
      loadW9Status();
    } catch (e) { flash("error", e?.response?.data?.error || "Upload failed."); }
  };

  const genEmployment = async (save) => {
    if (!emp.employeeName.trim()) { flash("warning", "Enter the employee's name."); return; }
    setBusy(true);
    try {
      try {
        localStorage.setItem("letterSigner", JSON.stringify(
          { signerName: emp.signerName, signerTitle: emp.signerTitle }));
      } catch { /* a browser that blocks storage must not block the letter */ }
      await openPdf("post", "/companyDocuments/employment-letter/generate", { ...emp, save });
      if (save) flash("success", "Letter generated and saved to the Library.");
    } catch (e) { flash("error", e?.response?.data?.error || "Failed to generate the letter."); }
    finally { setBusy(false); }
  };

  const genLetterhead = async (save) => {
    setBusy(true);
    try {
      await openPdf("post", "/companyDocuments/letterhead/generate", { ...lh, save });
      if (save) flash("success", "Letterhead generated and saved to the Library.");
    } catch (e) { flash("error", e?.response?.data?.error || "Failed to generate the letterhead."); }
    finally { setBusy(false); }
  };

  return (
    <Grid container spacing={2}>
      {/* Resale certificate */}
      <Grid item xs={12} md={7}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" sx={{ mb: 0.5 }}>Texas Resale Certificate</Typography>
            <Typography variant="caption" color="text.secondary">
              Your company block is filled automatically. Enter the seller (supplier) details for this order.
            </Typography>
            <Stack spacing={2} sx={{ mt: 2 }}>
              <TextField label="Seller name" size="small" value={rc.sellerName}
                onChange={(e) => setRc({ ...rc, sellerName: e.target.value })} required />
              <TextField label="Seller street address" size="small" value={rc.sellerStreet}
                onChange={(e) => setRc({ ...rc, sellerStreet: e.target.value })} />
              <TextField label="Seller city, state, ZIP" size="small" value={rc.sellerCityStateZip}
                onChange={(e) => setRc({ ...rc, sellerCityStateZip: e.target.value })} />
              <TextField label="Description of items" size="small" multiline minRows={2} value={rc.itemsDescription}
                onChange={(e) => setRc({ ...rc, itemsDescription: e.target.value })} />
              <TextField label="Business activity" size="small" multiline minRows={2} value={rc.businessActivity}
                onChange={(e) => setRc({ ...rc, businessActivity: e.target.value })} />
              <Grid container spacing={2}>
                <Grid item xs={7}>
                  <TextField select fullWidth label="Signed by" size="small" value={rc.signatureId}
                    onChange={(e) => setRc({ ...rc, signatureId: e.target.value })}>
                    <MenuItem value=""><em>— none —</em></MenuItem>
                    {signatures.map((s) => (
                      <MenuItem key={s.Signature_ID} value={s.Signature_ID}>
                        {s.SignerName}{s.Title ? ` — ${s.Title}` : ""}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={5}>
                  <TextField type="date" fullWidth label="Date" size="small" InputLabelProps={{ shrink: true }}
                    value={rc.date} onChange={(e) => setRc({ ...rc, date: e.target.value })} />
                </Grid>
              </Grid>
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" startIcon={<ViewIcon />} disabled={busy} onClick={() => genResale(false)}>
                  Preview PDF
                </Button>
                <Button variant="contained" disabled={busy} onClick={() => genResale(true)}>
                  Generate &amp; Save
                </Button>
              </Stack>
              {!signatures.length && (
                <Typography variant="caption" color="text.secondary">
                  Tip: add a signature in the Signatures tab to stamp it on the certificate.
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      {/* W-9 */}
      <Grid item xs={12} md={5}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" sx={{ mb: 0.5 }}>W-9</Typography>
            <Typography variant="caption" color="text.secondary">
              Upload a <b>blank</b> W-9 once (your company info filled, but no date or signature).
              Pick a signer and we stamp the signature + today's date on each copy.
            </Typography>
            <Box sx={{ mt: 2 }}>
              {w9.status && w9.status.exists ? (
                <Chip color="success" size="small" label={`Template ready: ${w9.status.template?.FileName || "W-9.pdf"}`} sx={{ mb: 2 }} />
              ) : (
                <Alert severity="info" sx={{ mb: 2 }}>No W-9 template uploaded yet.</Alert>
              )}
              <input ref={w9FileRef} type="file" accept="application/pdf" hidden
                onChange={(e) => uploadW9(e.target.files?.[0])} />
              <Stack spacing={2}>
                <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => w9FileRef.current?.click()}>
                  {w9.status?.exists ? "Replace W-9 template" : "Upload blank W-9"}
                </Button>
                <TextField select label="Signed by" size="small" value={w9.signatureId}
                  onChange={(e) => setW9({ ...w9, signatureId: e.target.value })}>
                  <MenuItem value=""><em>— none —</em></MenuItem>
                  {signatures.map((s) => (
                    <MenuItem key={s.Signature_ID} value={s.Signature_ID}>
                      {s.SignerName}{s.Title ? ` — ${s.Title}` : ""}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField type="date" label="Date" size="small" InputLabelProps={{ shrink: true }}
                  value={w9.date} onChange={(e) => setW9({ ...w9, date: e.target.value })} />
                <Stack direction="row" spacing={1}>
                  <Button variant="outlined" startIcon={<ViewIcon />} disabled={busy || !w9.status?.exists}
                    onClick={() => genW9(false)}>Preview</Button>
                  <Button variant="contained" disabled={busy || !w9.status?.exists}
                    onClick={() => genW9(true)}>Generate &amp; Save</Button>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  If the date lands in the wrong spot, adjust its position in the Company Info tab.
                </Typography>
              </Stack>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Employment verification */}
      <Grid item xs={12} md={7}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" sx={{ mb: 0.5 }}>Employment Verification</Typography>
            <Typography variant="caption" color="text.secondary">
              The letter a landlord, a bank or an immigration office asks for. Pay is printed
              only if you fill it in — leave it blank when nobody asked for it.
            </Typography>
            <Stack spacing={2} sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={7}>
                  <TextField fullWidth label="Employee name" size="small" required
                    value={emp.employeeName}
                    onChange={(e) => setEmp({ ...emp, employeeName: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <TextField fullWidth label="Position" size="small" placeholder="Yard Operator"
                    value={emp.position}
                    onChange={(e) => setEmp({ ...emp, position: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField select fullWidth label="Employment" size="small"
                    value={emp.employmentType}
                    onChange={(e) => setEmp({ ...emp, employmentType: e.target.value })}>
                    {["Full-time", "Part-time", "Contract", "Seasonal"].map((t) => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField type="date" fullWidth label="Start date" size="small"
                    InputLabelProps={{ shrink: true }} value={emp.startDate}
                    onChange={(e) => setEmp({ ...emp, startDate: e.target.value })} />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField type="date" fullWidth label="End date" size="small"
                    InputLabelProps={{ shrink: true }} value={emp.endDate}
                    helperText={emp.endDate ? "Reads as a past employee" : "Blank = still working"}
                    onChange={(e) => setEmp({ ...emp, endDate: e.target.value })} />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField fullWidth label="Income (optional)" size="small" placeholder="800"
                    value={emp.payRate}
                    helperText={emp.payRate
                      ? (/hour/i.test(emp.payPeriod) ? "Prints as: Pay rate" : "Prints as: Gross income")
                      : "Blank = no money on the letter"}
                    onChange={(e) => setEmp({ ...emp, payRate: e.target.value })} />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField select fullWidth label="Per" size="small" value={emp.payPeriod}
                    disabled={!emp.payRate}
                    onChange={(e) => setEmp({ ...emp, payPeriod: e.target.value })}>
                    {["per week", "every two weeks", "per month", "per year", "per hour"].map((t) => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField type="date" fullWidth label="Letter date" size="small"
                    InputLabelProps={{ shrink: true }} value={emp.date}
                    onChange={(e) => setEmp({ ...emp, date: e.target.value })} />
                </Grid>
              </Grid>
              <TextField label="Addressed to" size="small"
                placeholder={'Blank = "To Whom It May Concern"'}
                value={emp.addressedTo}
                onChange={(e) => setEmp({ ...emp, addressedTo: e.target.value })} />
              <TextField label="Extra notes (optional)" size="small" multiline minRows={2}
                value={emp.notes}
                onChange={(e) => setEmp({ ...emp, notes: e.target.value })} />
              <Divider textAlign="left" sx={{ pt: 1 }}>
                <Typography variant="caption" color="text.secondary">Signed by</Typography>
              </Divider>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={7}>
                  <TextField fullWidth label="Name printed under the signature" size="small"
                    placeholder="Abdul Mateen" value={emp.signerName}
                    onChange={(e) => setEmp({ ...emp, signerName: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <TextField fullWidth label="Title" size="small" placeholder="Manager"
                    value={emp.signerTitle}
                    onChange={(e) => setEmp({ ...emp, signerTitle: e.target.value })} />
                </Grid>
              </Grid>
              <TextField select label="Stamp a saved signature (optional)" size="small"
                value={emp.signatureId}
                helperText="Leave as none to sign the printed letter by hand."
                onChange={(e) => setEmp({ ...emp, signatureId: e.target.value })}>
                <MenuItem value=""><em>— none —</em></MenuItem>
                {signatures.map((s) => (
                  <MenuItem key={s.Signature_ID} value={s.Signature_ID}>
                    {s.SignerName}{s.Title ? ` — ${s.Title}` : ""}
                  </MenuItem>
                ))}
              </TextField>
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" startIcon={<ViewIcon />} disabled={busy}
                  onClick={() => genEmployment(false)}>Preview PDF</Button>
                <Button variant="contained" disabled={busy}
                  onClick={() => genEmployment(true)}>Generate &amp; Save</Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      {/* Company letterhead */}
      <Grid item xs={12} md={5}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" sx={{ mb: 0.5 }}>Company Letterhead</Typography>
            <Typography variant="caption" color="text.secondary">
              Your company heading on a blank sheet — print it and write on it, or type the
              letter below and it comes out ready to sign.
            </Typography>
            <Stack spacing={2} sx={{ mt: 2 }}>
              <TextField label="Letter text (optional)" size="small" multiline minRows={8}
                placeholder="Leave empty for a blank sheet."
                value={lh.body}
                onChange={(e) => setLh({ ...lh, body: e.target.value })} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={7}>
                  <TextField select fullWidth label="Print the date" size="small"
                    value={lh.showDate ? "yes" : "no"}
                    onChange={(e) => setLh({ ...lh, showDate: e.target.value === "yes" })}>
                    <MenuItem value="yes">Yes</MenuItem>
                    <MenuItem value="no">No — blank sheet</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={5}>
                  <TextField type="date" fullWidth label="Date" size="small"
                    InputLabelProps={{ shrink: true }} value={lh.date} disabled={!lh.showDate}
                    onChange={(e) => setLh({ ...lh, date: e.target.value })} />
                </Grid>
              </Grid>
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" startIcon={<ViewIcon />} disabled={busy}
                  onClick={() => genLetterhead(false)}>Preview PDF</Button>
                <Button variant="contained" disabled={busy}
                  onClick={() => genLetterhead(true)}>Generate &amp; Save</Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

/* ============================ Library ============================ */
function LibraryTab({ flash }) {
  // On a phone the table was wider than the screen and would not scroll, so
  // View / Download / Delete sat off the right-hand edge with no way to reach
  // them. Below 600px the rows become cards instead, with the three buttons
  // written out in full - nothing to scroll to, and a bigger target to press
  // with a thumb in the yard.
  const isMobile = useMediaQuery("(max-width:600px)");
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const fileRef = useRef();
  const [pending, setPending] = useState(null); // { file, title, notes }

  const load = useCallback(() => {
    setLoading(true);
    api.get("/companyDocuments").then((r) => setDocs(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const doUpload = async () => {
    if (!pending?.file) return;
    const fd = new FormData();
    fd.append("file", pending.file);
    fd.append("Title", pending.title || pending.file.name);
    if (pending.notes) fd.append("Notes", pending.notes);
    try {
      await api.post("/companyDocuments/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      flash("success", "Document uploaded.");
      setPending(null); load();
    } catch (e) { flash("error", e?.response?.data?.error || "Upload failed."); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this document?")) return;
    await api.delete(`/companyDocuments/${id}`);
    load();
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6">Document Library</Typography>
          <>
            <input ref={fileRef} type="file" hidden accept="application/pdf,image/*"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) setPending({ file: f, title: f.name, notes: "" }); }} />
            <Button variant="contained" startIcon={<UploadIcon />} onClick={() => fileRef.current?.click()}>Upload</Button>
          </>
        </Stack>

        {loading ? <CircularProgress size={24} /> : isMobile ? (
          <Stack spacing={1.5}>
            {docs.map((d) => (
              <Paper key={d.Document_ID} variant="outlined" sx={{ p: 1.5 }}>
                <Typography sx={{ fontWeight: 600, wordBreak: "break-word" }}>
                  {d.Title || d.FileName}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, flexWrap: "wrap" }}>
                  <Chip size="small" label={d.DocType} />
                  <Typography variant="caption" color="text.secondary">
                    {new Date(d.CreatedAt).toLocaleDateString()}
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary"
                  sx={{ display: "block", mt: 0.5, wordBreak: "break-all" }}>
                  {d.FileName}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: "wrap", gap: 1 }}>
                  <Button size="small" variant="outlined" startIcon={<ViewIcon />}
                    onClick={() => window.open(authUrl(`/companyDocuments/${d.Document_ID}/download`), "_blank")}>
                    View
                  </Button>
                  <Button size="small" variant="outlined" startIcon={<DownloadIcon />}
                    onClick={() => window.open(authUrl(`/companyDocuments/${d.Document_ID}/download?download=1`), "_blank")}>
                    Download
                  </Button>
                  <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />}
                    onClick={() => remove(d.Document_ID)}>
                    Delete
                  </Button>
                </Stack>
              </Paper>
            ))}
            {!docs.length && (
              <Typography color="text.secondary" sx={{ py: 2 }}>No documents yet.</Typography>
            )}
          </Stack>
        ) : (
          <TableContainer>
          <Table size="small" sx={{ minWidth: 560 }}>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>File</TableCell>
                <TableCell>Added</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {docs.map((d) => (
                <TableRow key={d.Document_ID} hover>
                  <TableCell>{d.Title || d.FileName}</TableCell>
                  <TableCell><Chip size="small" label={d.DocType} /></TableCell>
                  <TableCell>{d.FileName}</TableCell>
                  <TableCell>{new Date(d.CreatedAt).toLocaleDateString()}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="View"><IconButton size="small"
                      onClick={() => window.open(authUrl(`/companyDocuments/${d.Document_ID}/download`), "_blank")}>
                      <ViewIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Download"><IconButton size="small"
                      onClick={() => window.open(authUrl(`/companyDocuments/${d.Document_ID}/download?download=1`), "_blank")}>
                      <DownloadIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => remove(d.Document_ID)}>
                      <DeleteIcon fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {!docs.length && <TableRow><TableCell colSpan={5}><Typography color="text.secondary" sx={{ py: 2 }}>No documents yet.</Typography></TableCell></TableRow>}
            </TableBody>
          </Table>
          </TableContainer>
        )}
      </CardContent>

      <Dialog open={!!pending} onClose={() => setPending(null)} fullWidth maxWidth="sm">
        <DialogTitle>Upload document</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2">{pending?.file?.name}</Typography>
            <TextField label="Title" size="small" value={pending?.title || ""}
              onChange={(e) => setPending({ ...pending, title: e.target.value })} />
            <TextField label="Notes (optional)" size="small" multiline minRows={2} value={pending?.notes || ""}
              onChange={(e) => setPending({ ...pending, notes: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPending(null)}>Cancel</Button>
          <Button variant="contained" onClick={doUpload}>Upload</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

/* ============================ Signatures ============================ */
function SignaturesTab({ signatures, reload, flash }) {
  const [open, setOpen] = useState(false);

  const remove = async (id) => {
    if (!window.confirm("Delete this signature?")) return;
    await api.delete(`/companyDocuments/signatures/${id}`);
    reload();
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6">Saved Signatures</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>Add signature</Button>
        </Stack>
        <Grid container spacing={2}>
          {signatures.map((s) => (
            <Grid item xs={12} sm={6} md={4} key={s.Signature_ID}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ height: 70, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#fafafa", borderRadius: 1, mb: 1 }}>
                    <img alt={s.SignerName} style={{ maxHeight: 64, maxWidth: "100%" }}
                      src={authUrl(`/companyDocuments/signatures/${s.Signature_ID}/image`)} />
                  </Box>
                  <Typography variant="subtitle2">{s.SignerName}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.Title || " "}</Typography>
                  <Box sx={{ textAlign: "right" }}>
                    <IconButton size="small" color="error" onClick={() => remove(s.Signature_ID)}><DeleteIcon fontSize="small" /></IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
          {!signatures.length && <Grid item xs={12}><Typography color="text.secondary">No signatures saved yet.</Typography></Grid>}
        </Grid>
      </CardContent>
      <AddSignatureDialog open={open} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); reload(); flash("success", "Signature saved."); }} flash={flash} />
    </Card>
  );
}

function AddSignatureDialog({ open, onClose, onSaved, flash }) {
  const [mode, setMode] = useState("draw"); // draw | upload
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const canvasRef = useRef();
  const drawing = useRef(false);

  useEffect(() => { if (open) { setName(""); setTitle(""); setFile(null); setMode("draw"); } }, [open]);

  // --- canvas drawing ---
  const pos = (e) => {
    const c = canvasRef.current; const r = c.getBoundingClientRect();
    const t = e.touches?.[0];
    return { x: (t ? t.clientX : e.clientX) - r.left, y: (t ? t.clientY : e.clientY) - r.top };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.lineWidth = 2.2; ctx.lineCap = "round"; ctx.strokeStyle = "#111"; ctx.lineTo(p.x, p.y); ctx.stroke(); };
  const end = () => { drawing.current = false; };
  const clearCanvas = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); };

  const save = async () => {
    if (!name.trim()) { flash("warning", "Enter the signer's name."); return; }
    try {
      if (mode === "upload") {
        if (!file) { flash("warning", "Choose an image file."); return; }
        const fd = new FormData();
        fd.append("image", file); fd.append("signerName", name); fd.append("title", title);
        await api.post("/companyDocuments/signatures", fd, { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        const dataUrl = canvasRef.current.toDataURL("image/png");
        await api.post("/companyDocuments/signatures", { signerName: name, title, imageDataUrl: dataUrl });
      }
      onSaved();
    } catch (e) { flash("error", e?.response?.data?.error || "Failed to save signature."); }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add signature</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Signer name" size="small" value={name} onChange={(e) => setName(e.target.value)} required />
          <TextField label="Title (e.g. CEO)" size="small" value={title} onChange={(e) => setTitle(e.target.value)} />
          <Stack direction="row" spacing={1}>
            <Button size="small" variant={mode === "draw" ? "contained" : "outlined"} startIcon={<DrawIcon />} onClick={() => setMode("draw")}>Draw</Button>
            <Button size="small" variant={mode === "upload" ? "contained" : "outlined"} startIcon={<UploadIcon />} onClick={() => setMode("upload")}>Upload image</Button>
          </Stack>
          {mode === "draw" ? (
            <Box>
              <Box sx={{ border: "1px dashed #bbb", borderRadius: 1 }}>
                <canvas ref={canvasRef} width={460} height={140}
                  style={{ width: "100%", height: 140, touchAction: "none", cursor: "crosshair" }}
                  onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                  onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
              </Box>
              <Button size="small" onClick={clearCanvas} sx={{ mt: 0.5 }}>Clear</Button>
            </Box>
          ) : (
            <Button variant="outlined" component="label" startIcon={<UploadIcon />}>
              {file ? file.name : "Choose image (PNG/JPG)"}
              <input type="file" accept="image/*" hidden onChange={(e) => setFile(e.target.files?.[0])} />
            </Button>
          )}
          <Typography variant="caption" color="text.secondary">
            Tip: a PNG with a transparent background looks best on the form.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

/* ============================ Company Info ============================ */
function ProfileTab({ profile, setProfile, reload, flash }) {
  const [form, setForm] = useState(profile || {});
  useEffect(() => { setForm(profile || {}); }, [profile]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    try {
      const r = await api.put("/companyDocuments/profile", form);
      setProfile(r.data); reload(); flash("success", "Company info saved.");
    } catch (e) { flash("error", e?.response?.data?.error || "Save failed."); }
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>Company Info</Typography>
        <Typography variant="caption" color="text.secondary">
          These fill the top block of the resale certificate and your W-9 details.
        </Typography>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Legal name" value={form.LegalName || ""} onChange={set("LegalName")} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Phone" value={form.Phone || ""} onChange={set("Phone")} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Address" value={form.Address || ""} onChange={set("Address")} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="City, State, ZIP" value={form.CityStateZip || ""} onChange={set("CityStateZip")} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Sales-tax permit #" value={form.PermitNumber || ""} onChange={set("PermitNumber")} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="EIN (for W-9)" value={form.EIN || ""} onChange={set("EIN")} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Tax classification" value={form.TaxClassification || ""} onChange={set("TaxClassification")} /></Grid>
          <Grid item xs={12}><TextField fullWidth size="small" label="Default business activity" multiline minRows={2} value={form.DefaultBusinessActivity || ""} onChange={set("DefaultBusinessActivity")} /></Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>W-9 date position</Typography>
        <Typography variant="caption" color="text.secondary">
          Where today's date is stamped on your W-9 template (PDF points from the bottom-left). Adjust if the date is misaligned.
        </Typography>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={4}><TextField fullWidth size="small" type="number" label="X" value={form.W9DateX ?? 415} onChange={set("W9DateX")} /></Grid>
          <Grid item xs={4}><TextField fullWidth size="small" type="number" label="Y" value={form.W9DateY ?? 231} onChange={set("W9DateY")} /></Grid>
          <Grid item xs={4}><TextField fullWidth size="small" type="number" label="Font size" value={form.W9FontSize ?? 11} onChange={set("W9FontSize")} /></Grid>
        </Grid>

        <Box sx={{ mt: 3 }}>
          <Button variant="contained" onClick={save}>Save</Button>
        </Box>
      </CardContent>
    </Card>
  );
}
